import { useToaster } from '@innovationdepartment/proxima-ui';
import useBrandTagMappingApi from 'hooks/useBrandTagMappingsApi';
import { useEffect, useState } from 'react';
import { useBrandStore } from 'stores';
import BrandTagsView from './BrandTags.View';
import RemoveTagConfirmation from './RemoveTagConfirmation.Container';

type BrandTag = {
  id: string;
  originalName: string;
  displayName: string;
  category: string;
  subCategory: string;
};

const BrandTagsContainer = () => {
  const { brand } = useBrandStore();
  const [brandTags, setBrandTags] = useState<BrandTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [tagToRemove, setTagToRemove] = useState<{ id: string; displayName: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const { fetchTagsForBrand, removeTagFromBrand } = useBrandTagMappingApi();
  const { showToaster } = useToaster();

  const fetchBrandTags = async () => {
    if (!brand?.brandId) return;
    setLoading(true);
    const tags = await fetchTagsForBrand(brand.brandId);
    setBrandTags(tags);
    setLoading(false);
  };

  const handleRemoveTag = (tagId: string) => {
    const tag = brandTags.find(t => t.id === tagId);
    if (tag) {
      setTagToRemove({ id: tag.id, displayName: tag.displayName });
      setIsConfirmationOpen(true);
    }
  };

  const confirmRemoveTag = async () => {
    if (!brand?.brandId || !tagToRemove) return;
    setIsRemoving(true);
    const response = await removeTagFromBrand(brand.brandId, tagToRemove.id);
    if (response?.error) {
      setIsConfirmationOpen(false);
      setIsRemoving(false);
      return;
    }
    await fetchBrandTags();
    showToaster({ message: 'Tag removed from brand.', variant: 'success' });
    setTagToRemove(null);
    setIsConfirmationOpen(false);
    setIsRemoving(false);
  };

  const closeConfirmation = () => {
    setIsConfirmationOpen(false);
    setTagToRemove(null);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    fetchBrandTags();
  }, [brand?.brandId]);

  return (
    <>
      <BrandTagsView brandTags={brandTags} loading={loading} onRemoveTag={handleRemoveTag} />
      {!!tagToRemove && (
        <RemoveTagConfirmation
          open={isConfirmationOpen}
          close={closeConfirmation}
          confirm={confirmRemoveTag}
          tagDisplayName={tagToRemove.displayName}
          isLoading={isRemoving}
        />
      )}
    </>
  );
};

export default BrandTagsContainer;
